import React from 'react';
import { useNavigate } from 'react-router-dom';
import StatusBadge from './StatusBadge';
// Map backend stage to display name for badge
const getStageDisplayName = (stage) => {
  if (!stage) return '';
  const map = {
    REGISTRATION: 'APPLIED',
    HR_SHORTLISTING: 'SHORTLISTED',
    AGREEMENT: 'AGREEMENT',
    APPLICATION: 'APPLICATION',
    INTERVIEW: 'INTERVIEW',
    OFFER_LETTER: 'OFFER LETTER',
    PAYMENT: 'PAYMENT',
    DOCUMENT_VERIFICATION: 'DOCUMENT VERIFICATION',
    EXAM: 'EXAM',
    MEDICAL: 'MEDICAL',
    TPC_PRACTISING: 'TPC PRACTISING',
    ESD_APPROVAL: 'ESD APPROVAL',
    VISA_PROCESS: 'VISA PROCESS',
    TICKET: 'TICKET',
    DEPARTURE: 'DEPARTURE',
    COMPLETED: 'COMPLETED',
    REJECTED: 'REJECTED',
  };
  return map[stage] || stage.replace(/_/g, ' ');
};

const ApplicationCard = ({ application, showCandidate = false }) => {
  const navigate = useNavigate();
  const applicationId = application._id || application.id;
  const getShortApplicationId = () => {
    const explicitId = application?.applicationId || application?.referenceId;
    if (explicitId) {
      return String(explicitId).toUpperCase();
    }

    if (!applicationId) return 'N/A';

    // Use the same formatting logic as admin view
    const rawId = applicationId.toString();
    const country = (application?.destinationCountry || '').trim();
    const countryPrefix = country
      ? country.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase().padEnd(2, 'X')
      : 'XX';

    const stableSuffix = rawId.slice(-6).toUpperCase();
    return `${countryPrefix}${stableSuffix}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewDetails = (e) => {
    e.stopPropagation(); // Prevent event bubbling

    if (!applicationId) {
      console.error('No application ID found!');
      return;
    }

    navigate(`/applications/${applicationId}`);
  };

  return (
    <div
      className="bg-white rounded-xl p-5 shadow-[0_2px_8px_rgba(0,0,0,0.05)] transition-all duration-300 border border-[#E0E0E0] hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)] hover:-translate-y-0.5 md:p-4"
    >
      <div className="flex justify-between items-start mb-4 gap-3 md:flex-col md:gap-2">
        <div>
          <h3 className="m-0 mb-1 text-lg font-semibold text-primary md:text-base">
            {application.jobPosting}
          </h3>
          {showCandidate && application.candidateId && (
            <p className="m-0 text-sm text-text-secondary font-medium">
              {application.candidateId.firstName} {application.candidateId.lastName}
            </p>
          )}
        </div>
        <StatusBadge stage={getStageDisplayName(application.currentStage)} />
      </div>

      <div className="flex flex-col gap-2 mb-4">
        <div className="flex justify-between items-center text-sm">
          <span className="text-text-secondary font-medium">Application ID:</span>
          <span className="text-text-primary font-normal break-all">{getShortApplicationId()}</span>
        </div>

        <div className="flex justify-between items-center text-sm">
          <span className="text-text-secondary font-medium">Applied:</span>
          <span className="text-text-primary font-normal">{formatDate(application.appliedAt)}</span>
        </div>

        {application.completedAt && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-text-secondary font-medium">Completed:</span>
            <span className="text-text-primary font-normal">{formatDate(application.completedAt)}</span>
          </div>
        )}

        {showCandidate && application.candidateId && (
          <>
            <div className="flex justify-between items-center text-sm">
              <span className="text-text-secondary font-medium">Email:</span>
              <span className="text-text-primary font-normal">{application.candidateId.email}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-text-secondary font-medium">Phone:</span>
              <span className="text-text-primary font-normal">{application.candidateId.phoneNumber}</span>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end pt-3 border-t border-[#F0F0F0]">
        <button
          onClick={handleViewDetails}
          className="bg-transparent border-none text-accent-green font-semibold text-sm cursor-pointer px-4 py-2 rounded-sm transition-all duration-200 hover:bg-[rgba(107,127,92,0.1)]"
        >
          View Details →
        </button>
      </div>
    </div>
  );
};

export default ApplicationCard;
